import {useEffect, useCallback, useState} from 'react';
import {
  useProductOptions,
  isBrowser,
  useUrl,
  AddToCartButton,
  Money,
  OptionWithValues,
  useCart,
} from '@shopify/hydrogen';
import {
  Text,
  Button,
  ProductOptions,
  IconCart,
  IconSpinner,
  IconCartFail,
  IconDelivery,
  IconInStore,
  InternalLink,
} from '~/components';
import {translateProductOptionName} from '~/lib/utils';

export function ProductForm({
  isOnlyOrderProduct = false,
}: {
  isOnlyOrderProduct: boolean;
}) {
  const {pathname, search} = useUrl();
  const [params, setParams] = useState(new URLSearchParams(search));
  const [productCount, setProductCount] = useState(1);
  const {status} = useCart();
  const [addingItem, setAddingItem] = useState(false);
  const [itemAdded, setItemAdded] = useState(false);

  const {options, setSelectedOption, selectedOptions, selectedVariant} =
    useProductOptions();

  console.log(selectedVariant);
  const isOnSale =
    Number(selectedVariant?.price?.amount) <
      Number(selectedVariant?.compareAtPrice?.amount) || false;
  const quantityAvailable =
    (selectedVariant?.quantityAvailable as unknown as number) || 0;
  const isOutOfStock = quantityAvailable < 1;
  const backOrderAllowed = selectedVariant?.currentlyNotInStock || false;
  const onlyInStore = isOutOfStock && !backOrderAllowed;
  const showDeliveryTime = !isOutOfStock || (isOutOfStock && backOrderAllowed);
  const showAvailableNow = !isOutOfStock || onlyInStore;

  useEffect(() => {
    if (addingItem && status === 'idle') {
      setAddingItem(false);
      setItemAdded(true);
      setTimeout(() => setItemAdded(false), 2000);
    }
  }, [status, addingItem]);

  useEffect(() => {
    if (params || !search) return;
    setParams(new URLSearchParams(search));
  }, [params, search]);

  useEffect(() => {
    (options as OptionWithValues[]).map(({name, values}) => {
      if (!params) return;
      const currentValue = params.get(name.toLowerCase()) || null;
      if (currentValue) {
        const matchedValue = values.filter(
          (value) => encodeURIComponent(value.toLowerCase()) === currentValue,
        );
        setSelectedOption(name, matchedValue[0]);
      } else {
        params.set(
          encodeURIComponent(name.toLowerCase()),
          encodeURIComponent(selectedOptions![name]!.toLowerCase()),
        ),
          window.history.replaceState(
            null,
            '',
            `${pathname}?${params.toString()}`,
          );
      }
    });
  }, []);

  useEffect(() => {
    if (productCount < 1) {
      setProductCount(1);
    }
  }, [productCount]);

  const handleChange = useCallback(
    (name: string, value: string) => {
      setSelectedOption(name, value);
      if (!params) return;
      params.set(
        encodeURIComponent(name.toLowerCase()),
        encodeURIComponent(value.toLowerCase()),
      );
      if (isBrowser()) {
        window.history.replaceState(
          null,
          '',
          `${pathname}?${params.toString()}`,
        );
      }
    },
    [setSelectedOption, params, pathname],
  );

  return (
    <form className="grid gap-10">
      <div className="flex flex-col">
        {isOnSale && (
          <Money
            withoutTrailingZeros
            data={selectedVariant?.compareAtPrice!}
            as="span"
            className="text-xs font-clash text-textTertiary line-through pb-1"
          />
        )}
        <Money
          withoutTrailingZeros
          data={selectedVariant?.priceV2!}
          as="span"
          className="text-3xl font-clash"
        />
      </div>

      {options?.length > 0 && (
        <div className="grid gap-4">
          {(options as OptionWithValues[]).map(({name, values}) => {
            if (values.length === 1) {
              return null;
            }
            return (
              <div
                key={name}
                className="flex flex-col flex-wrap mb-4 gap-y-2 last:mb-0"
              >
                <Text as="label" size="lead" className="text-sm">
                  {translateProductOptionName(name)}
                </Text>
                <div className="flex flex-wrap items-baseline gap-4">
                  <ProductOptions
                    name={name}
                    handleChange={handleChange}
                    values={values}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex text-xs">
        {showAvailableNow && !isOnlyOrderProduct && (
          <div className="flex items-center mr-6">
            <span className="block w-[14px] h-[14px] rounded-full bg-successGreen mr-1"></span>
            Saatavilla heti
          </div>
        )}
        {showDeliveryTime && !isOnlyOrderProduct && (
          <div className="flex items-center">
            <IconDelivery className="w-4 mr-1" /> Toimitusaika{' '}
            {isOutOfStock ? <>1-3 arkipäivää.</> : '0-3 arkipäivää'}
          </div>
        )}
        {isOnlyOrderProduct && (
          <div className="flex items-center">
            <IconDelivery className="w-4 mr-1" /> Tilaustuote.
            Toimitusaika-arvio 7–14 arkipäivää.
          </div>
        )}
      </div>
      {onlyInStore && (
        <div className="flex items-center bg-white p-4">
          <div>
            <IconInStore className="mr-4 w-[24px] h-[24px]" customSize={true} />
          </div>
          <div>
            Tämä tuote on saatavilla ainoastaan&nbsp;
            <InternalLink
              documentType="page"
              slug="yhteystiedot"
              className="underline"
            >
              myymälässä
            </InternalLink>
            .
          </div>
        </div>
      )}
      {!onlyInStore && (
        <div className="grid gap-4 grid-cols-shopping-cart-mobile lg:grid-cols-shopping-cart">
          <div className="grid grid-cols-[1fr_1fr_1fr] min-h-[3em] min-w-[10em] bg-white">
            <div
              className="flex justify-center items-center border border-black cursor-pointer"
              onClick={() => setProductCount(productCount - 1)}
            >
              -
            </div>
            <div className="flex flex-row justify-center items-center border-t border-b border-black font-bold">
              {productCount}
            </div>
            <div
              className="flex justify-center items-center border border-black cursor-pointer"
              onClick={() => setProductCount(productCount + 1)}
            >
              +
            </div>
          </div>
          <AddToCartButton
            variantId={selectedVariant?.id}
            quantity={productCount}
            accessibleAddingToCartLabel="Lisätään ostoskoriin"
            disabled={onlyInStore}
            type="button"
            onClick={() => setAddingItem(true)}
          >
            <Button
              width="full"
              variant={
                itemAdded ? 'accent' : onlyInStore ? 'disabled' : 'primary'
              }
              className={itemAdded ? 'animate-addedToCart' : ''}
              as="span"
            >
              {addingItem ? (
                <Text
                  as="span"
                  className="flex items-center justify-center gap-2 mx-auto"
                >
                  <IconSpinner className="text-white animate-spin" />
                </Text>
              ) : itemAdded ? (
                <Text
                  as="span"
                  className="flex items-center justify-center gap-2 mx-auto"
                >
                  <span>Lisätty ostoskoriin</span>
                </Text>
              ) : (
                !onlyInStore && (
                  <Text
                    as="span"
                    className="flex items-center justify-center gap-2 mx-auto"
                  >
                    <IconCart />
                    <span>Lisää ostoskoriin</span>
                  </Text>
                )
              )}
              {onlyInStore && (
                <Text
                  as="span"
                  className="flex items-center justify-center gap-2 mx-auto"
                >
                  <IconCartFail />
                  Vain myymälässä
                </Text>
              )}
            </Button>
          </AddToCartButton>
        </div>
      )}
    </form>
  );
}
